import { MkIcon } from "@/mk/MkIcon";
import { useCallback, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { MkField } from "./MkField";
import { MkRecordPicker } from "./MkRecordPicker";
import type { PickerColumn, PickerSource } from "./MkRecordPicker";
import { MkRelDropdown } from "./MkRelDropdown";
import { useRelSearch } from "./useRelSearch";
import type { RelRecord, RelSearch } from "./useRelSearch";

export interface InfoItem {
    label: string;
    value: string;
    badge?: boolean;
    color?: string;
}

interface Props {
    value?: RelRecord | null | false;
    search: RelSearch;
    browse?: PickerSource;
    cacheKey?: string;
    label?: string;
    hint?: string;
    error?: string;
    required?: boolean;
    placeholder?: string;
    canCreate?: boolean;
    canOpen?: boolean;
    limit?: number;
    disabled?: boolean;
    info?: InfoItem[];
    pickerColumns?: PickerColumn[];
    pickerTitle?: string;
    className?: string;
    onUpdate?(value: RelRecord | false): void;
    onCreate?(name: string): Promise<RelRecord | null>;
    onCreateNew?(name: string): Promise<RelRecord | null>;
    onOpen?(record: RelRecord): void;
    onEdit?(record: RelRecord): Promise<RelRecord | null>;
}

export function MkMany2One({ value, search, browse, cacheKey = "", label, hint, error, required,
                             placeholder, canCreate = false, canOpen = true, limit = 8, disabled,
                             info = [], pickerColumns = [], pickerTitle, className, onUpdate,
                             onCreate, onCreateNew, onOpen, onEdit }: Props) {
    const { t } = useTranslation();
    const input = useRef<HTMLInputElement>(null);
    const control = useRef<HTMLDivElement>(null);
    const [editing, setEditing] = useState(false);
    const [picker, setPicker] = useState(false);
    const rel = useRelSearch(search, limit, cacheKey);
    const record = value || null;
    const anchor = useCallback(() => control.current, []);

    const searchPlaceholder = placeholder
        || (label ? t("mk.rel.searchFor", { label }) : t("mk.rel.search"));
    const moreLabel = rel.state.hasMore || !rel.state.results.length ? t("mk.rel.searchMore") : "";
    const createLabel = rel.showCreate(canCreate)
        ? t("mk.rel.create", { name: rel.state.query.trim() }) : "";
    const displayValue = editing ? rel.state.query : (record ? record.name : "");
    const openable = canOpen !== false && Boolean(record && record.id);
    const editable = Boolean(onEdit) && Boolean(record) && !disabled;

    function select(next: RelRecord | null) {
        onUpdate?.(next ? { id: next.id, name: next.name } : false);
        rel.reset();
        setEditing(false);
    }

    function clear() {
        onUpdate?.(false);
        rel.reset();
        setEditing(true);
        window.setTimeout(() => input.current?.focus(), 0);
    }

    function openPicker() {
        rel.close();
        setPicker(true);
    }

    async function createOne() {
        const query = rel.state.query.trim();
        // the list closes before a create form may open over it
        rel.close();
        input.current?.blur();
        const created = onCreateNew ? await onCreateNew(query) : (onCreate ? await onCreate(query) : null);
        if (created) {
            select(created);
        }
    }

    function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            rel.move(event.key === "ArrowDown" ? 1 : -1);
        } else if (event.key === "Enter") {
            event.preventDefault();
            const active = rel.activeRecord();
            if (active) {
                select(active);
            } else if (rel.showCreate(canCreate)) {
                void createOne();
            }
        } else if (event.key === "Escape") {
            if (rel.state.open || rel.state.query) {
                event.preventDefault();
                event.stopPropagation();
            }
            setEditing(false);
            rel.reset();
            event.currentTarget.blur();
        } else if (event.key === "Tab") {
            rel.close();
        }
    }

    return (
        <MkField label={label} hint={hint} error={error} required={required} className={className}>
            <div className={`mk-m2m mk-m2o${disabled ? " mk-m2m-disabled" : ""}`}>
                <div className="mk-m2m-control" ref={control}>
                    <input ref={input} className="mk-m2m-input" type="text" autoComplete="off"
                           placeholder={searchPlaceholder} value={displayValue} disabled={disabled}
                           onChange={(event) => {
                               setEditing(true);
                               rel.onInput(event.target.value);
                           }}
                           onFocus={() => {
                               setEditing(true);
                               const initial = record ? record.name : "";
                               rel.patch({ query: initial });
                               rel.openList({ query: initial });
                               window.setTimeout(() => input.current?.select(), 0);
                           }}
                           onBlur={() => {
                               rel.onBlur();
                               window.setTimeout(() => {
                                   setEditing(false);
                                   rel.patch({ query: "" });
                               }, 160);
                           }}
                           onMouseDown={(event) => {
                               if (disabled) {
                                   return;
                               }
                               if (document.activeElement === input.current) {
                                   event.preventDefault();
                                   rel.onPointerDown();
                               }
                           }}
                           onKeyDown={onKeyDown} />
                    {info.length > 0 && record && (
                        <div className="mk-m2o-info">
                            {info.map((item, index) => (
                                <span className="mk-m2o-info-item" key={index}>
                                    <span className="mk-m2o-info-label">{item.label}</span>
                                    {item.badge
                                        ? <span className={`mk-m2o-info-badge is-${item.color || "secondary"}`}>{item.value}</span>
                                        : <span className="mk-m2o-info-value">{item.value}</span>}
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="mk-m2o-actions">
                        {!disabled && (
                            <button type="button" className="mk-m2o-btn mk-m2o-browse"
                                    title={t("mk.rel.browse")}
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        openPicker();
                                    }}>
                                <MkIcon name="fa-th-list" />
                            </button>
                        )}
                        {record && (
                            <>
                                {editable ? (
                                    <button type="button" className="mk-m2o-btn mk-m2o-edit"
                                            title={t("mk.rel.edit")}
                                            onMouseDown={async (event) => {
                                                event.preventDefault();
                                                const updated = await onEdit?.(record);
                                                if (updated) {
                                                    onUpdate?.(updated);
                                                }
                                            }}>
                                        <MkIcon name="fa-pencil" />
                                    </button>
                                ) : openable ? (
                                    <button type="button" className="mk-m2o-btn mk-m2o-open"
                                            title={t("mk.rel.open")}
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                onOpen?.(record);
                                            }}>
                                        <MkIcon name="fa-external-link" />
                                    </button>
                                ) : null}
                                {!disabled && (
                                    <button type="button" className="mk-m2o-btn mk-m2o-clear"
                                            title={t("mk.clear")}
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                clear();
                                            }}>
                                        <MkIcon name="fa-times" />
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <MkRelDropdown open={rel.state.open && !disabled} anchor={anchor}
                               items={rel.state.results} active={rel.state.active}
                               loading={rel.state.loading}
                               emptyLabel={t("mk.rel.noResults")} createLabel={createLabel}
                               moreLabel={moreLabel} hintLabel={t("mk.rel.hint")}
                               onPick={(item) => select(item)}
                               onHover={(index) => rel.patch({ active: index })}
                               onCreate={() => void createOne()}
                               onMore={openPicker} />
            </div>
            {picker && (
                <MkRecordPicker visibilityKey={cacheKey ? `relation-picker:${cacheKey}` : undefined} open title={pickerTitle || label || t("mk.rel.selectRecord")}
                                source={browse ?? ((query, _page, size) => search(query, size)
                                    .then((rows) => ({ rows: rows.map((row) => ({ ...row })), total: rows.length })))}
                                columns={pickerColumns} multi={false}
                                canCreate={canCreate && Boolean(onCreateNew || onCreate)}
                                onCreateNew={(query) => (onCreateNew ?? onCreate)?.(query) ?? Promise.resolve(null)}
                                onSelect={(rows) => {
                                    const first = rows[0];
                                    if (first) {
                                        select(first);
                                    }
                                }}
                                onClose={() => {
                                    setPicker(false);
                                    setEditing(false);
                                }} />
            )}
        </MkField>
    );
}
